import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { CATCH_SHOUT_CATCH_CONFIG } from './constants';

export interface CatchShoutCatchHostFallingProps {
  animals: string[];
  themeTitle: string;
  active: boolean;
  roundCatches?: { A: number; B: number };
}

interface FallingAnimal {
  id: number;
  emoji: string;
  xPercent: number;
  yPercent: number;
  speedMultiplier: number;
}

const FALL_SPEED = CATCH_SHOUT_CATCH_CONFIG.fallSpeedPerSecond;
const SPAWN_INTERVAL = CATCH_SHOUT_CATCH_CONFIG.spawnIntervalMs;
const SPAWN_COUNT_MAX = CATCH_SHOUT_CATCH_CONFIG.spawnCountMax;

function pickAnimal(animals: string[]): string {
  return animals[Math.floor(Math.random() * animals.length)] ?? '🐱';
}

function randomSpeedMultiplier(): number {
  const { fallSpeedMultiplierMin, fallSpeedMultiplierMax } = CATCH_SHOUT_CATCH_CONFIG;
  return fallSpeedMultiplierMin + Math.random() * (fallSpeedMultiplierMax - fallSpeedMultiplierMin);
}

export const CatchShoutCatchHostFalling: React.FC<CatchShoutCatchHostFallingProps> = ({
  animals,
  themeTitle,
  active,
  roundCatches,
}) => {
  const { t } = useTranslation();
  const [falling, setFalling] = useState<FallingAnimal[]>([]);
  const nextId = useRef(0);
  const lastSpawn = useRef(0);
  const lastTick = useRef(0);

  useEffect(() => {
    if (!active) {
      setFalling([]);
      return;
    }
    lastSpawn.current = Date.now();
    lastTick.current = Date.now();
  }, [active, animals]);

  useEffect(() => {
    if (!active || !animals.length) return;
    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastSpawn.current < SPAWN_INTERVAL) return;
      lastSpawn.current = now;
      const count = 1 + Math.floor(Math.random() * SPAWN_COUNT_MAX);
      const newOnes: FallingAnimal[] = [];
      for (let i = 0; i < count; i++) {
        newOnes.push({
          id: nextId.current++,
          emoji: pickAnimal(animals),
          xPercent: 8 + Math.random() * 84,
          yPercent: 0,
          speedMultiplier: randomSpeedMultiplier(),
        });
      }
      setFalling((prev) => [...prev, ...newOnes]);
    }, 250);
    return () => clearInterval(interval);
  }, [active, animals]);

  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const tick = () => {
      const now = Date.now();
      const dt = Math.min((now - lastTick.current) / 1000, 0.1);
      lastTick.current = now;
      setFalling((prev) => {
        const next: FallingAnimal[] = [];
        for (const a of prev) {
          const newY = a.yPercent + FALL_SPEED * a.speedMultiplier * dt * 100;
          if (newY >= 105) continue;
          next.push({ ...a, yPercent: newY });
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const previewEmojis = (animals ?? []).slice(0, 8).join(' ');

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 auto',
        minHeight: 0,
        gap: '1rem',
      }}
    >
      <div style={{ textAlign: 'center', flexShrink: 0 }}>
        <div
          className="bonus-host-glow-text"
          style={{
            fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)',
            fontWeight: 700,
            marginBottom: '0.5rem',
          }}
        >
          {themeTitle}
        </div>
        <div
          className="bonus-host-glow-text--muted"
          style={{
            fontSize: 'clamp(0.95rem, 2vw, 1.25rem)',
            fontWeight: 500,
          }}
        >
          {t('gameplay.catchShoutCatch.hostHint')}
        </div>
      </div>

      {(roundCatches?.A !== undefined || roundCatches?.B !== undefined) && (
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            flexShrink: 0,
            fontSize: 'clamp(1rem, 2vw, 1.35rem)',
            fontWeight: 700,
          }}
        >
          <span className="bonus-host-glow-text">
            {t('gameplay.catchShoutCatch.teamRound', { team: 'A', count: roundCatches?.A ?? 0 })}
          </span>
          <span className="bonus-host-glow-text">
            {t('gameplay.catchShoutCatch.teamRound', { team: 'B', count: roundCatches?.B ?? 0 })}
          </span>
        </div>
      )}

      <div
        style={{
          flex: '1 1 auto',
          minHeight: 200,
          width: '100%',
          maxWidth: 900,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 20,
          border: '4px solid rgba(52, 211, 153, 0.35)',
          background: 'rgba(52, 211, 153, 0.08)',
        }}
      >
        {falling.map((a) => (
          <div
            key={a.id}
            style={{
              position: 'absolute',
              left: `${a.xPercent}%`,
              top: `${a.yPercent}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: 'clamp(2rem, 4vw, 3.5rem)',
              lineHeight: 1,
              pointerEvents: 'none',
            }}
          >
            {a.emoji}
          </div>
        ))}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 12,
            background: 'rgba(52, 211, 153, 0.5)',
            borderTop: '3px solid rgba(52, 211, 153, 0.8)',
          }}
        />
      </div>

      <div
        style={{
          flexShrink: 0,
          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
          letterSpacing: '0.05em',
          opacity: 0.85,
        }}
      >
        {previewEmojis || '🐱 🐕 🐦'}
      </div>
    </div>
  );
};
